import React, { useCallback, useEffect, useState, useMemo } from 'react';

import Card from './components/AgendaContent/Card';
import EmptyAgenda from './components/AgendaContent/EmptyAgenda';
import AgendaFilters from './components/AgendaFilters';
import Loader from './components/AgendaContent/Loader';
import FirstMonthSeperator from './components/AgendaContent/FirstMonthSeperator';
import {
	today,
	getWeekNumber,
	sortByDate,
	hasMatchingShowTags,
	dbDateToTimestamp,
} from '../agenda/utils/helpers';
import CourseCard from './components/AgendaContent/CourseCard';

export const DaysLib = [
	{monday: 'Maandag'},
	{tuesday: 'Dinsdag'},
	{wednesday: 'Woensdag'},
	{thursday: 'Donderdag'},
	{friday: 'Vrijdag'},
	{saturday: 'Zaterdag'},
];

/**
	* @description		Core functionality for filtering the agenda
*/
const Filter = ({
	config,
	eventFeed,
	availableLocations,
	availableDates,
	availableTags,
	availableAudiences,
	availableCategories,
	rootElement,
}) => {

	const [showEmptyAgenda, setShowEmptyAgenda] = useState(false);

	const [filteredElements, setFilteredElements] = useState([]); //result of the filtered data
	const [filteredCount, setFilteredCount] = useState(null);
	const [cards, setCards] = useState([]); //state that holds the rendered components
	const [rendering, setRendering] = useState(true);
	const [showModal, setShowModal] = useState(false);

	//state reflecting params that tweak the filtering
	const [activeFilterDate, setActiveFilterDate] = useState(false); //boolean (false = 'alle dagen'; true = checkout activeTimeStamp)
	const [activeTimestamp, setActiveTimeStamp] = useState(today); //becomes a timestamp when date filtering is used (Only one, not an array)
	const [dateRange, setDateRange] = useState(null); //'month' or 'week' or 'weekend'
	const [activeLocations, setActiveLocations] = useState([]); //empty array or one or more cinema as filter
	const [activeTags, setActiveTags] = useState([]);
	const [activeAudiences, setActiveAudiences] = useState([]);
	const [activeCategories, setActiveCategories] = useState([]);
	const [activeWeekdays, setActiveWeekdays] = useState([]); //only used for courses (inside tags in feed)
	const [lastChanged, setLastChanged] = useState(null);
	const [isFree, setIsFree] = useState(false); //false means all events, true means only free events
	const [isDiscount, setIsDiscount] = useState(false); //false means all events, true means only events with discount

	// some available filtering options are disabled based on other options
	const [availableLocationsFiltered, setAvailableLocationsFiltered] = useState(availableLocations);
	const [availableAudiencesFiltered, setAvailableAudiencesFiltered] = useState(availableAudiences);
	const [availableCategoriesFiltered, setAvailableCategoriesFiltered] = useState(availableCategories);
	const [availableTagsFiltered, setAvailableTagsFiltered] = useState(availableTags);

	// memo's to avoid unnecessary rerender filterbar
	const memoizedFilteredCount = useMemo(() => filteredCount, [filteredCount]);
	const memoizedActiveFilterDate = useMemo(() => activeFilterDate, [activeFilterDate]);
	const memoizedActiveTimestamp = useMemo(() => activeTimestamp, [activeTimestamp]);
	const memoizedDateRange = useMemo(() => dateRange, [dateRange]);
	const memoizedActiveLocations = useMemo(() => activeLocations, [activeLocations]);
	const memoizedActiveCategories = useMemo(() => activeCategories, [activeCategories]);
	const memoizedActiveAudiences = useMemo(() => activeAudiences, [activeAudiences]);
	const memoizedActiveTags = useMemo(() => activeTags, [activeTags]);
	const memoizedIsFree = useMemo(() => isFree, [isFree]);
	const memoizedIsDiscount = useMemo(() => isDiscount, [isDiscount]);

	const memoizedAvailableLocationsFiltered = useMemo(() => availableLocationsFiltered, [availableLocationsFiltered]);
	const memoizedAvailableCategoriesFiltered = useMemo(() => availableCategoriesFiltered, [availableCategoriesFiltered]);
	const memoizedAvailableAudiencesFiltered = useMemo(() => availableAudiencesFiltered, [availableAudiencesFiltered]);
	const memoizedAvailableTagsFiltered = useMemo(() => availableTagsFiltered, [availableTagsFiltered]);
	const memoizedActiveWeekdays = useMemo(() => activeWeekdays, [activeWeekdays]);

	const updateUrl = useCallback(() => {
		const urlParams = new URLSearchParams(); // added the window.location.search so the link is preserved as is
		// console.log(urlParams);
		// const activeDay = activeFilterDate ? activeTimestamp : 'all';
		if (activeFilterDate && activeTimestamp) urlParams.set('f-day', encodeURI(activeTimestamp.toString()));
		if (dateRange) urlParams.set('f-date-range', encodeURI(dateRange.toString()));
		if (activeLocations.length > 0) urlParams.set('f-location', encodeURI(activeLocations.map(location => Object.keys(location)).join('+')));
		if (activeTags.length > 0) urlParams.set('f-tag', encodeURI(activeTags.map(tag => Object.keys(tag)).join('+')));
		if (activeAudiences.length > 0) urlParams.set('f-audience', encodeURI(activeAudiences.map(audience => Object.keys(audience)).join('+')));
		if (activeWeekdays.length > 0) urlParams.set('f-weekday', encodeURI(activeWeekdays.map(weekday => Object.keys(weekday)).join('+')));
		if (activeCategories.length > 0) urlParams.set('f-category', encodeURI(activeCategories.map(category => Object.keys(category)).join('+')));
		if (isFree) urlParams.set('f-free', encodeURI(isFree.toString()));
		if (isDiscount) urlParams.set('f-discount', encodeURI(isDiscount.toString()));
		if (urlParams.size > 0 ) window.history.replaceState(null, '', '?' + urlParams.toString());
	}, [activeFilterDate, activeTimestamp, activeLocations, activeAudiences, activeCategories, activeTags, isFree, isDiscount]);

	const setDateParam = (value) => {
		if (value === 'all') {
			setActiveFilterDate(false);
			setActiveTimeStamp(null);
		} else {
			setActiveFilterDate(true);
			setActiveTimeStamp(value);
		}
		filterAvailableParameters();
		setLastChanged('date');
  	};

	const setDateRangeParam = (value) => {
		if (['month', 'week', 'weekend', null].includes(value)) setDateRange(value);
	}

	const setLocationsParam = (value) => {
		if (activeLocations.includes(value)) {
			setActiveLocations([...activeLocations].filter((locationOption) => Object.keys(locationOption)[0] !== Object.keys(value)[0]));
		} else {
			setActiveLocations([...activeLocations].concat(value));
		}
		setLastChanged('location');
  };

	const setTagsParam = useCallback((value, onlyActivate) => {
		if (activeTags.includes(value)) {
			!onlyActivate && setActiveTags([...activeTags].filter((tagOption) => Object.keys(tagOption)[0] !== Object.keys(value)[0]))
		} else {
			setActiveTags([...activeTags].concat(value));
		}
		setLastChanged('tag');
	}, [activeTags]);

	const setCoursesTagsParam = (value) => {
		setActiveTags([value]);
	}

	const setAudiencesParam = (value) => {
		if (activeAudiences.includes(value)) {
			setActiveAudiences([...activeAudiences].filter((audienceOption) => Object.keys(audienceOption)[0] !== Object.keys(value)[0]));
		} else {
			setActiveAudiences([...activeAudiences].concat(value));
		}
		setLastChanged('audience');
	};

	const setCoursesAudienceParam = (value) => {
		if (activeAudiences.includes(value)) {
			setActiveAudiences([]);
		} else {
			setActiveAudiences([value]);
		}
	}

	const setCoursesWeekdaysParam = (value) => {
		if (activeWeekdays.includes(value)) {
			setActiveWeekdays([]);
		} else {
			setActiveWeekdays([value]);
		}
	}

	const setCategoriesParam = (value) => {
		if (activeCategories.includes(value)) {
			setActiveCategories([...activeCategories].filter((category) => Object.keys(category)[0] !== Object.keys(value)[0]));
		} else {
			setActiveCategories([...activeCategories].concat(value));
		}
		setLastChanged('category');
	};

	const setFreeParam = (value) => {
		setIsFree(value);
	}

	const setDiscountParam = (value) => {
		setIsDiscount(value);
	}

   const resetParams = () => {
		setActiveFilterDate(false);
		setActiveTimeStamp(today);
		setActiveLocations([]);
		setActiveAudiences([]);
		setActiveCategories([]);
		setActiveTags([]);
		setActiveWeekdays([]);
		setIsFree(false);
		setIsDiscount(false);
		setLastChanged('reset');
  	};

	/**
	 * @description	On setting param, update the available filtering options
	 */
	const filterAvailableParameters = useCallback(() => {
		if (lastChanged === 'reset') {
			setAvailableLocationsFiltered(availableLocations);
			setAvailableAudiencesFiltered(availableAudiences);
			setAvailableCategoriesFiltered(availableCategories);
			setAvailableTagsFiltered(availableTags);
			return;
		}
		if (lastChanged !== 'location') {
			setAvailableLocationsFiltered(availableLocations.filter((location) => filteredElements.some((element) => element.times.some((show) => Object.keys(show.location)[0] === Object.keys(location)[0]))));
		}
		if (lastChanged !== 'tag') {
			setAvailableTagsFiltered(availableTags.filter((tag) => filteredElements.some((element) => Object.keys(element.tags[config.tag_name]).indexOf(Object.keys(tag)[0]) > -1)));
		}
		if (lastChanged !== 'audience') {
			setAvailableAudiencesFiltered(availableAudiences.filter((audience) => filteredElements.some((element) => Object.keys(element.tags?.audiences).indexOf(Object.keys(audience)[0]) > -1)));
		}
		if (lastChanged !== 'category') {
			setAvailableCategoriesFiltered(availableCategories.filter((category) => filteredElements.some((element) => Object.keys(element.tags?.categories).indexOf(Object.keys(category)[0]) > -1)));
		}
	}, [availableLocations, availableAudiences, availableCategories, availableTags, filteredElements, lastChanged]);

	/**
	 * @description	Main filtering functionality, activated by a change in params through useEffect below
	 */
   const filterAccordingToCurrentParams = useCallback((input) => {
		let copyInput = structuredClone(input);

		// filtering elements whether tag exists in the elements tags OR any shows with that tag exist
		let resultTagFilter = [];
		if (activeTags.length > 0) {
			copyInput.forEach((element) => {
				let matchingTag = false;
				activeTags.forEach((tag) => {
					if (Object.keys(element.tags[config.tag_name]).indexOf(Object.keys(tag)[0]) > -1) matchingTag = true;
				});
				if (matchingTag) {
					resultTagFilter.push(element);
				} else {
					const matchingShowTags = hasMatchingShowTags(element, config.no_special_tab_tags);
					if (matchingShowTags) resultTagFilter.push(element);
				}
			});
		} else {
			resultTagFilter = structuredClone(copyInput);
		}

		// filtering elements whether audience exists in the elements tags
		let resultAudienceFilter = [];
		if (activeAudiences.length > 0) {
			resultTagFilter.forEach((element) => {
				let matchingAudience = false;
				activeAudiences.forEach((audience) => {
					if (Object.keys(element.tags?.audiences).indexOf(Object.keys(audience)[0]) > -1) matchingAudience = true;
				});
				if (matchingAudience) {
					resultAudienceFilter.push(element);
				}
			});
		} else {
			resultAudienceFilter = structuredClone(resultTagFilter);
		}

		// filtering elements whether weekday exists in the elements tags
		let resultWeekdayFilter = [];
		if (activeWeekdays.length > 0) {
			resultAudienceFilter.forEach((element) => {
				let matchingWeekday = false;
				activeWeekdays.forEach((weekday) => {
					if (Object.keys(element.tags?.day).indexOf(Object.keys(weekday)[0]) > -1) matchingWeekday = true;
				});
				if (matchingWeekday) {
					resultWeekdayFilter.push(element);
				}
			});
		} else {
			resultWeekdayFilter = structuredClone(resultAudienceFilter);
		}

		// filtering elements whether category exists in the elements tags
		let resultCategoryFilter = [];
		if (activeCategories.length > 0) {
			resultWeekdayFilter.forEach((element) => {
				let matchingCategory = false;
				activeCategories.forEach((category) => {
					if (Object.keys(element.tags?.categories).indexOf(Object.keys(category)[0]) > -1) matchingCategory = true;
				});
				if (matchingCategory) {
					resultCategoryFilter.push(element);
				}
			});
		} else {
			resultCategoryFilter = structuredClone(resultWeekdayFilter);
		}

		// filtering elements based on date AND return filtered times array for date
		let resultFilterDate = [];
		if (activeFilterDate && activeTimestamp) {
			resultCategoryFilter.forEach((element) => {
				const elementCopy = structuredClone(element);
				if (config.date_filter_range && dateRange) {
					if (dateRange === 'month') {
						elementCopy.times = elementCopy.times.filter((show) => new Date(show.date).getMonth() === new Date(activeTimestamp).getMonth());
					} else if (dateRange === 'week') {
						elementCopy.times = elementCopy.times.filter((show) => getWeekNumber(show.date) === getWeekNumber(activeTimestamp));
					} else if (dateRange === 'weekend') {
						elementCopy.times = elementCopy.times.filter((show) => {
							if (getWeekNumber(show.date) !== getWeekNumber(activeTimestamp)) return show; //future weeks
							if (new Date(show.date).getDay() === 6 || new Date(show.date).getDay() === 7) return show; //this week & weekend
						});
					}
				} else {
					if (config.results_beyond_date) { // Flint
						elementCopy.times = elementCopy.times.filter((show) => show.date >= activeTimestamp);
					} else { //Bioscopen
						elementCopy.times = elementCopy.times.filter((show) => show.date === activeTimestamp);
					}
				}
				resultFilterDate.push(elementCopy);
			});
		} else {
			resultFilterDate = structuredClone(resultCategoryFilter);
		}

		// filtering elements on location (only adjust shows array)
		let resultFilterLocations = [];
		if (activeLocations.length > 0) {
			resultFilterDate.forEach((element) => {
				const elementCopy = structuredClone(element);
				elementCopy.times = elementCopy.times.filter((show) => activeLocations.some(location => Object.keys(location)[0] === Object.keys(show.location)[0]));
				resultFilterLocations.push(elementCopy);
			});
		} else {
			resultFilterLocations = structuredClone(resultFilterDate);
		}

		//filtering based on free or discount
		let resultFilterFree = [];
		if (isFree || isDiscount) {
			resultFilterLocations.forEach((element) => {
				const elementCopy = structuredClone(element);
				elementCopy.times = elementCopy.times.filter((show) => {
					if (isFree && show.free) return show;
					if (isDiscount && show.discount) return show;
				});
				resultFilterFree.push(elementCopy);
			});
		} else {
			resultFilterFree = structuredClone(resultFilterLocations);
		}

		return resultFilterFree;
	}, [activeFilterDate, activeTimestamp, activeLocations, activeAudiences, activeWeekdays, isFree, isDiscount, activeCategories, activeTags, config, dateRange]);

	/**
	 * @description	Main filter (reacting to a change in params)
	 */
	useEffect(() => {
		if (!eventFeed) return;
		const filterDataObj = structuredClone(eventFeed);
		let resultsFiltering;
		if (config.sort_by_date) {
			resultsFiltering = filterAccordingToCurrentParams(sortByDate(filterDataObj.filter((el) => el['times'] && el['times'].length > 0)));
		} else {
			resultsFiltering = filterAccordingToCurrentParams(filterDataObj.filter((el) => el['times'] && el['times'].length > 0));
		}
		const elementsWithTimes = resultsFiltering.filter((el) => el['times'] && el['times'].length > 0);
		setFilteredCount(elementsWithTimes.length);
		setFilteredElements(elementsWithTimes);
	}, [eventFeed, activeFilterDate, activeTimestamp, activeLocations, activeTags, activeCategories, activeAudiences, activeWeekdays, isDiscount, isFree, filterAccordingToCurrentParams]);


	/**
	 *
	 * @param {*} elements filtered elements
	 * @returns {*} JSX holding all the cards and month seperators
	 */
	const getAgendaListingWithMonths = (elements) => {
		const monthTracker = []; // months that have rendered [{month: 1, year: 2021}, {month: 2, year: 2021}]
		return filteredElements.map((element, index) => {

			let isFirstOfMonth = false;
			if ('date_range' in element && element.date_range.length > 0) {
				const timestamp = dbDateToTimestamp(element.date_range[0]);
				const month = new Date(timestamp).getMonth();
				const year = new Date(timestamp).getFullYear();
				let monthInTracker = false;
				monthTracker.forEach((trackedMonth) => {
					if (trackedMonth.month === month && trackedMonth.year === year) {
						monthInTracker = true;
					}
				});
				if (!monthInTracker) {
					monthTracker.push({ month: month, year: year });
					isFirstOfMonth = true;
				}
			}
			return (
				<li key={element.post_id + '-' + index}>
					{isFirstOfMonth &&
					<div className="first-month-seperator">
						<FirstMonthSeperator/>
						<p className="h5 first-month-seperator__text">{new Date(dbDateToTimestamp(element.date_range[0])).toLocaleDateString('nl', { month: 'long', year: 'numeric' })} </p>
					</div>
					}
					<Card element={element} />
				</li>
			);
		});
	}


	/**
	 * @description	Renders the ReactElement cards based on the filteredElements
	 */


	useEffect(() => {
		setRendering(true);
		filterAvailableParameters();
		if (config.agenda_listing_with_months) {
			setCards(getAgendaListingWithMonths(filteredElements));
		} else {

			setCards(filteredElements.map((element, index) => {
				return (

					<li key={element.post_id + '-' + index}>
						{/* replace this with course type card component when ready */}
						<CourseCard element={element} />
					</li>

				);
			}));

		}
		setRendering(false);
  	}, [activeFilterDate, activeTimestamp, config, filteredElements, setTagsParam]);

	/**
	 * @description	Writes the current parameters to the url
	 */
	useEffect(() => {
		if (!rendering) updateUrl();
	}, [activeFilterDate, activeTimestamp, activeLocations, activeAudiences, activeWeekdays, activeTags, activeCategories, isFree, isDiscount, rendering, updateUrl]);


	/**
	 * @description	Gets the parameters from url and sets the state accordingly
	 */
	useEffect(() => {
		if (eventFeed && window.location.search) {
			const queryParams = new URLSearchParams(window.location.search);

			if (queryParams.has('f-day')) {
				const param = decodeURI(queryParams.get('f-day') ?? '');
				if (availableDates.includes(Number(param))) {
					setActiveFilterDate(true);
					setActiveTimeStamp(Number(param));
				};
				// if (param === 'all') setActiveFilterDate(false);
			}

			if (queryParams.has('f-date-range')) {
				const param = decodeURI(queryParams.get('f-date-range') ?? '');
				if (['month', 'week', 'weekend'].includes(param)) setDateRange(param);
			}

			if (queryParams.has('f-location')) {
				const slugs = decodeURI(queryParams.get('f-location') ?? '').split('+');
				const params = availableLocations.filter((location) => slugs.includes(Object.keys(location)[0]));
				if (params.length > 0) setActiveLocations(params);
			}

			if (queryParams.has('f-tag')) {
				const slugs = decodeURI(queryParams.get('f-tag') ?? '').split('+');
				const params = availableTags.filter((tag) => slugs.includes(Object.keys(tag)[0]));
				if (params.length > 0) setActiveTags(params);
			}

			if (queryParams.has('f-audience')) {
				const slugs = decodeURI(queryParams.get('f-audience') ?? '').split('+');
				const params = availableAudiences.filter((audience) => slugs.includes(Object.keys(audience)[0]));
				if (params.length > 0) setActiveAudiences(params);
			}

			if (queryParams.has('f-weekday')) {
				const slugs = decodeURI(queryParams.get('f-weekday') ?? '').split('+');
				const params = DaysLib.filter((weekday) => slugs.includes(Object.keys(weekday)[0]));
				if (params.length > 0) setActiveWeekdays(params);
			}

			if (queryParams.has('f-category')) {
				const slugs = decodeURI(queryParams.get('f-category') ?? '').split('+');
				const params = availableCategories.filter((category => slugs.includes(Object.keys(category)[0])));
				if (params.length > 0) setActiveCategories(params);
			}
			if (queryParams.has('f-free')) setIsFree(queryParams.get('f-free') === 'true');
			if (queryParams.has('f-discount')) setIsDiscount(queryParams.get('f-discount') === 'true');
		}
	}, [eventFeed, availableLocations, availableDates, availableTags, availableAudiences, availableCategories]);

	useEffect(() => {
		if (cards.length < 1) {
		  const timer = setTimeout(() => {
			 setShowEmptyAgenda(true);
		  }, 1000); // Vertraging van 1 seconden

		  // Opruimen bij unmount of als de kaarten veranderen
		  return () => clearTimeout(timer);
		} else {
		  setShowEmptyAgenda(false);
		}
	 }, [cards]);

  	if (rendering && cards.length ==0 ) return (
		<div className="loader__container--agenda">
			<Loader />
		</div>
	);

	return (
		<>

			<AgendaFilters
				memoizedFilteredCount={memoizedFilteredCount}
				memoizedActiveFilterDate={memoizedActiveFilterDate}
				memoizedActiveTimestamp={memoizedActiveTimestamp}
				memoizedDateRange={memoizedDateRange}
				memoizedActiveLocations={memoizedActiveLocations}
				memoizedActiveCategories={memoizedActiveCategories}
				memoizedActiveAudiences={memoizedActiveAudiences}
				memoizedActiveTags={memoizedActiveTags}
				memoizedActiveWeekdays={memoizedActiveWeekdays}
				memoizedIsFree={memoizedIsFree}
				memoizedIsDiscount={memoizedIsDiscount}

				availableDates={availableDates}
				availableLocations={availableLocations}
				availableCategories={availableCategories}
				availableAudiences={availableAudiences}
				availableTags={availableTags}

				memoizedAvailableLocationsFiltered={memoizedAvailableLocationsFiltered}
				memoizedAvailableCategoriesFiltered={memoizedAvailableCategoriesFiltered}
				memoizedAvailableAudiencesFiltered={memoizedAvailableAudiencesFiltered}
				memoizedAvailableTagsFiltered={memoizedAvailableTagsFiltered}

				setDateParam={setDateParam}
				setDateRangeParam={setDateRangeParam}
				setLocationsParam={setLocationsParam}
				setTagsParam={setTagsParam}
				setAudiencesParam={setAudiencesParam}
				setCoursesAudienceParam={setCoursesAudienceParam}
				setCategoriesParam={setCategoriesParam}
				setCoursesTagsParam={setCoursesTagsParam}
				setCoursesWeekdaysParam={setCoursesWeekdaysParam}
				setFreeParam={setFreeParam}
				setDiscountParam={setDiscountParam}
				resetParams={resetParams}

				config={config}
				showModal={showModal}
				setShowModal={setShowModal}
			/>
{ showEmptyAgenda ? (
  <EmptyAgenda
    text={config.empty_agenda_text}
    subtext={config.empty_agenda_subtext}
  />
) : (
				<>
				<div className="course-overview__container">
				<ol className={`js_cards-container course-overview ${!rendering ? 'animated animated__fadeIn' : 'animated animated__preHidden'}`}>
					{cards}
				</ol>
				</div>
				</>
			)}
		</>
	);

};

export default React.memo(Filter);
